import React, { useState, useContext, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FilterContext } from "../FilterContextProvider";
import Input from "../Components/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import InventoryLayout from "./InventoryLayout";
import useProducts from "../hooks/useProducts";
import useAllProducts from "../hooks/useAllProducts";
import useDateSelections from "../hooks/useDateSelections";
import cn from "classnames";
import useSalesSummary from "../hooks/useSalesSummary";
import { UserContext } from "../Auth/UserContextProvider";

const InventoryHistory = () => {
  const [resultsLoading, setResultsLoading] = useState(false);
  const { filterData, setFilterData } = useContext(FilterContext);
  const api = useApi();
  const { productsToRender: allSalesProducts } = useSalesSummary("active");
  const { isAdmin } = useContext(UserContext);
  const {
    products,
    getProducts,
    pagy,
    currentPage,
    setCurrentPage,
    resetPagination,
    setSearchFilter,
  } = useProducts(true);

  const { allProducts, setAllProdsSearchFilter } = useAllProducts(true);

  const { dateOptions, handleDateSelect, renderCustomDateSelector } =
    useDateSelections();

  const [searchTimeout, setSearchTimeout] = useState();
  const [seeAll, setSeeAll] = useState(false);

  const handleSeeAll = () => {
    setSeeAll(!seeAll);
  };

  const displayedProducts = useMemo(() => {
    return seeAll ? allProducts : products;
  }, [seeAll, allProducts, products]);


  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setAllProdsSearchFilter(searchTimeout);
      setSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setSearchFilter, setAllProdsSearchFilter]);

  const reset = () => {
    setFilterData({
      ...filterData,
      start_date: "",
      end_date: "",
      date_button: "",
    });
    setSearchTimeout("");
  };

  const handlePageChange = (e) => {
    setCurrentPage(e.target.value);
  };

  useEffect(() => {
    getProducts(currentPage);
    setResultsLoading(true);
  }, [api, filterData, currentPage, getProducts]);

  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist and date range from the filter in order to view
        product adjustment history
      </div>
    );
  };

  const productsToRender = [...displayedProducts];

  productsToRender.forEach((prod) => {
    if (prod.size_other) {
      prod.product_size = prod.size_other;
    } else if (prod?.size?.name) {
      prod.product_size = prod.size.name;
    } else if (!prod.size) {
      prod.product_size = null;
    }
    const saleCopy = allSalesProducts.find(({ id }) => prod.id === id);
    if (saleCopy?.id) {
      prod.has_history = saleCopy.has_history;
      prod.purchases = saleCopy.purchases;
      prod.discount_quantity = saleCopy.discount_quantity;
      prod.damage_quantity = saleCopy.damage_quantity;
      prod.mail_order = saleCopy.mail_order;
      prod.ctm_comp = saleCopy.ctm_comp;
      prod.management_comp = saleCopy.management_comp;
      prod.band_comp_quantity = saleCopy.band_comp_quantity;
      prod.inventory_adjust = saleCopy.inventory_adjust;
      prod.event_count = saleCopy.event_count;
      prod.unavailable = saleCopy.unavailable;
      prod.current = saleCopy.current;
      prod.sold = saleCopy.sold;
    } else {
      prod.has_history = false;
      prod.purchases = 0;
      prod.discount_quantity = 0;
      prod.damage_quantity = 0;
      prod.mail_order = 0;
      prod.ctm_comp = 0;
      prod.management_comp = 0;
      prod.band_comp_quantity = 0;
      prod.inventory_adjust = 0;
      prod.event_count = 0;
      prod.unavailable = 0;
      prod.current = 0;
      prod.sold = 0;
    }
  });

  let count = 0;

  for (let i = 1; i < displayedProducts.length; i++) {
    if (displayedProducts[i - 1].name !== displayedProducts[i].name) {
      count++;
    }
    displayedProducts[i].productNumber = count;
  }

  const renderHistoryTable = () => {
    return (
      <div>
        <div className="mx-10 pt-8">
          <div className="text-black mb-2 text-lg font-medium pb-2">
            Inventory History
          </div>
          <div className="flex mb-4 text-sm">
            <div className="absolute mt-2 ml-2 text-black">
              <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
            </div>
            <div className="w-1/4 mr-4 text-sm">
              <Input
                onChange={handleSearchChange}
                value={searchTimeout}
                placeholder="Search for products"
                bigPaddingLeft
              />
            </div>
            <div className="flex text-sm w-1/4">
              <Select
                id="date"
                className="text-xs font-light w-full"
                placeholder="Select date range"
                options={dateOptions}
                onChange={handleDateSelect}
                value={dateOptions?.filter(
                  (each) => each.value === filterData.date_button
                )}
              />
            </div>
            {(searchTimeout ||
              filterData.start_date ||
              filterData.end_date ||
              filterData.date_button === "custom") && (
              <button
                onClick={reset}
                className="text-blue-500 font-normal italic text-xs pl-4"
              >
                Reset
              </button>
            )}
          </div>
          <div className="my-4 w-1/2">
            {filterData.date_button === "custom" && renderCustomDateSelector()}
          </div>
        </div>
        {allSalesProducts.length > 0 ? (
          <div>
            <form>
              <table>
                <thead className="text-xs font-light sticky top-0 bg-white">
                  <tr className="text-left border-b-2 h-11 text-center">
                    <td className="w-1/5 text-left px-6">PRODUCT</td>
                    <td className="w-thin">PER SHOW</td>
                    <td className="w-thin">TOTAL CURRENT</td>
                    <td className="w-thin">PURCHASES</td>
                    <td className="w-thin">SOLD</td>
                    <td className="w-thin">DISCOUNT</td>
                    <td className="w-thin">DAMAGE</td>
                    <td className="w-thin">MAIL ORDER</td>
                    <td className="w-thin">TM COMP</td>
                    <td className="w-thin">MGMT COMP</td>
                    <td className="w-thin">ARTIST COMP</td>
                    <td className="w-thin">UNAVAILABLE</td>
                    <td className="w-thin p-2">INVENTORY ADJUST</td>
                  </tr>
                </thead>
                <tbody>
                  {productsToRender.map((productToRender, i) => {
                    return (
                      <tr
                        key={productToRender.id}
                        className={
                          displayedProducts[i].productNumber % 2 === 1
                            ? "border-b text-sm h-11 bg-gray-100 text-center"
                            : "border-b text-sm h-11 text-center"
                        }
                      >
                        {productToRender.has_history && isAdmin ? (
                          <td className="text-left text-blue-500 px-6">
                            {productToRender.product_size ? (
                              <Link
                                to={`/inventory/history/${productToRender.id}`}
                              >
                                {productToRender.name} -{" "}
                                {productToRender.product_size}
                              </Link>
                            ) : (
                              <Link
                                to={`/inventory/history/${productToRender.id}`}
                              >
                                {productToRender.name}
                              </Link>
                            )}
                          </td>
                        ) : (
                          <>
                            {productToRender.product_size ? (
                              <td className="text-left px-6">
                                {productToRender.name} -
                                {productToRender.product_size}
                              </td>
                            ) : (
                              <td className="text-left px-6">
                                {productToRender.name}
                              </td>
                            )}
                          </>
                        )}
                        <td
                          className={
                            productToRender.sold < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`per_show_${i}`}
                        >
                          {productToRender.event_count
                            ? (
                                productToRender.sold /
                                productToRender.event_count
                              )?.toFixed(2)
                            : ""}
                        </td>
                        <td
                          className={cn("", {
                            "bg-red-300 rounded-md":
                              productToRender.current < 0,
                            "bg-yellow-200 rounded-md":
                              productToRender.current /
                                (productToRender.sold /
                                  productToRender.event_count) <
                                3 && productToRender.current > 0,
                          })}
                          id={`current_${i}`}
                        >
                          {productToRender.current}
                        </td>
                        <td
                          className={
                            productToRender.purchases < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`purchases_${i}`}
                        >
                          {productToRender.purchases}
                        </td>
                        <td
                          className={
                            productToRender.sold < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`sold_${i}`}
                        >
                          {productToRender.sold}
                        </td>
                        <td
                          className={
                            productToRender.discount_quantity < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`discount_quantity_${i}`}
                        >
                          {productToRender.discount_quantity}
                        </td>
                        <td
                          className={
                            productToRender.damage_quantity < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`damage_quantity_${i}`}
                        >
                          {productToRender.damage_quantity}
                        </td>
                        <td
                          className={
                            productToRender.mail_order < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`mail_order_${i}`}
                        >
                          {productToRender.mail_order}
                        </td>
                        <td
                          className={
                            productToRender.ctm_comp < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`ctm_comp_${i}`}
                        >
                          {productToRender.ctm_comp}
                        </td>
                        <td
                          className={
                            productToRender.management_comp < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`management_comp_${i}`}
                        >
                          {productToRender.management_comp}
                        </td>
                        <td
                          className={
                            productToRender.band_comp_quantity < 0
                              ? " bg-red-300 rounded-md"
                              : ""
                          }
                          id={`band_comp_quantity_${i}`}
                        >
                          {productToRender.band_comp_quantity}
                        </td>
                        <td id={`unavailable_${i}`}>
                          {productToRender.unavailable}
                        </td>
                        <td id={`inventory_adjust_${i}`}>
                          {productToRender.inventory_adjust}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </form>
          </div>
        ) : (
          <div className="mx-12 pb-16">
            {resultsLoading ? (
              <>Results are loading...</>
            ) : (
              <> There is no sales history over this date range</>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
          {filterData.artist_id
            ? renderHistoryTable()
            : renderSelectArtistMessage()}
        </div>
        {allSalesProducts.length > 0 && (
          <footer className="text-sm mt-4 mx-8 flex justify-between">
            <div className="ml-1 mr-8">
              Showing <span className="font-bold">{seeAll ? 1 : pagy.from} </span>to
              <span className="font-bold"> {seeAll ? pagy.count : pagy.to} </span>of
              <span className="font-bold"> {pagy.count} </span>results
            </div>
            {seeAll ? (
                <div className="mr-8"> Showing All Results </div>
              ) : (
                <div className="mr-8">
                  Page {pagy.page} of {pagy.last}
                </div>
              )}
            <div className="flex -mt-2 mb-4">
            <span>
                <Button
                  value={0}
                  className={seeAll ? "button-white-disabled button-md" : "button button-white button-md"}
                  onClick={handleSeeAll}
                  text="See All"
                  disabled={false}
                ></Button>
                </span>
            <span>
                <Button
                  className={
                    pagy.page === 1
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={resetPagination}
                  text="First"
                  disabled={pagy.page === 1}
                ></Button>
              </span>
              <span className="mx-1">
                <Button
                  value={pagy.prev}
                  className={
                    !pagy.prev || seeAll
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Prev"
                  disabled={!pagy.prev || seeAll}
                ></Button>
              </span>
              <span>
                <Button
                  value={pagy.next}
                  className={
                    !pagy.next || seeAll
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Next"
                  disabled={!pagy.next || seeAll}
                ></Button>
              </span>
              <span className="ml-1">
                <Button
                  value={pagy.last}
                  className={
                    pagy.page === pagy.last || seeAll
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Last"
                  disabled={pagy.page === pagy.last || seeAll}
                ></Button>
              </span>
            </div>
          </footer>
        )}
      </div>
    </InventoryLayout>
  );
};

export default InventoryHistory;
